import "./App.css";
import { Suspense, lazy, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";

import Header from "./sections/Header";
import Footer from "./sections/Footer";
import ProgressBar from "./components/ProgressBar";
import pageLoadingJob from "./jobs/PageLoadingJob";
import WhatsappBot from "./components/WhatsappBot";
// import TawkToChatbot from "./components/TawkToChatbot";
import AIChatbotWidget from "./components/AIChatbotWidget";

import { BannerProvider } from "./contexts/banner";
import PageRouteFallback from "./components/PageRouteFallback";

const Home = lazy(() => import("./pages/home/page"));
const PageNotFound = lazy(() => import("./pages/404"));
const PrivacyPolicy = lazy(() => import("./pages/legal/privacy-policy/page"));
const KYCVerification = lazy(() => import("./pages/legal/kyc-verification/page"));
const AcceptanceUserPolicy = lazy(() => import("./pages/legal/acceptance-user-policy/page"));
const ServiceLevelAgreement = lazy(() => import("./pages/legal/service-level-agreement/page"));
const ComputeAmdEpyc = lazy(() => import("./pages/compute/compute-amd-epyc/page"));
const ComputeIntelGold = lazy(() => import("./pages/compute/compute-intel-gold/page"));
const Networking = lazy(() => import("./pages/networking/page"));
const ObjectStorage = lazy(() => import("./pages/products/object-storage/page"));
const OneClickApps = lazy(() => import("./pages/one-click-apps/page"));
const Pricing = lazy(() => import("./pages/pricing/page"));
const StorageIntegrations = lazy(() => import("./pages/storage/integrations/page"));
const About = lazy(() => import("./pages/about/page"));
const Contact = lazy(() => import("./pages/contact/page"));
const Sitemap = lazy(() => import("./pages/sitemap/page"));
const Tutorials = lazy(() => import("./pages/resources/tutorials/page"));
const CompareProviders = lazy(() => import("./pages/resources/compare-providers/page"));
const DataCenterChennai = lazy(() => import("./pages/data-centers/chennai/page"));
const TestimonialsPage = lazy(() => import("./pages/testimonials/page"));

const ScrollToHashElement = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      return;
    }

    const element = document.getElementById(hash.slice(1));
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return null;
};

function App() {
  const pageLoadingJobCallback = useCallback(pageLoadingJob, []);

  return (
    <Router>
      <ScrollToTop />
      <ProgressBar job={pageLoadingJobCallback} />
      <ScrollToHashElement />

      <div className="bg-brand-mesh flex min-h-screen w-full flex-col items-center relative">
        {/* <TawkToChatbot /> */}
        <AIChatbotWidget />
        <WhatsappBot />
        <BannerProvider>
          <Header />

          <main className="flex w-full flex-1 flex-col items-center">
            <Suspense fallback={<PageRouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/compute/amd-epyc" element={<ComputeAmdEpyc />} />
                <Route path="/compute/intel-gold" element={<ComputeIntelGold />} />
                <Route path="/networking" element={<Networking />} />
                <Route path="/one-click-apps" element={<OneClickApps />} />
                <Route path="/storage/object" element={<Navigate to="/products/object-storage" />} />
                <Route path="/products/object-storage" element={<ObjectStorage />} />
                <Route path="/storage/integrations" element={<StorageIntegrations />} />
                <Route path="/data-centers/chennai" element={<DataCenterChennai />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/resources/tutorials" element={<Tutorials />} />
                <Route path="/resources/tutorials/:tutorialSlug" element={<Tutorials />} />
                <Route path="/resources/compare-providers" element={<CompareProviders />} />
                <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/legal/acceptance-usage-policy" element={<AcceptanceUserPolicy />} />
                <Route path="/legal/kyc-verification" element={<KYCVerification />} />
                <Route path="/legal/service-level-agreement" element={<ServiceLevelAgreement />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </Suspense>
          </main>

          <Footer />
        </BannerProvider>
      </div>
    </Router>
  );
}

export default App;
